import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';
import { Role } from '@shared/auth/AuthContext';

const TeacherDashboardPage = lazy(() => import('./modules/faculty/pages/TeacherDashboardPage'));
const TeachersManagerPage = lazy(() => import('./modules/faculty/pages/TeachersManagerPage'));
const StaffPage = lazy(() => import('./modules/staff/pages/StaffPage'));
const TeachingLoadPage = lazy(() => import('./modules/faculty/pages/TeachingLoadPage'));
const LeavesPage = lazy(() => import('./modules/leaves/pages/LeavesPage'));
const PayrollPage = lazy(() => import('./modules/payroll/pages/PayrollPage'));
const TutorMarketplacePage = lazy(() => import('./modules/faculty/pages/TutorMarketplacePage'));
const TutorReviewsPage = lazy(() => import('./modules/faculty/pages/TutorReviewsPage'));
const TeacherLiveSessionsPage = lazy(() => import('./modules/faculty/pages/TeacherLiveSessionsPage'));

const LoadingFallback: React.FC = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const HR_READ_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'accountant'];
const FACULTY_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher'];
const STAFF_ADMIN_ROLES: Role[] = ['owner', 'admin', 'branch_head'];
const PAYROLL_ROLES: Role[] = ['owner', 'admin', 'accountant'];

export const hrRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="dashboard" replace />,
  },
  {
    path: 'dashboard',
    handle: { crumb: 'Faculty Command Center' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={HR_READ_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TeacherDashboardPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'teachers',
    handle: { crumb: 'Faculty Directory' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={FACULTY_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TeachersManagerPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'staff',
    handle: { crumb: 'All Staff Directory' },
    element: (
      <RoutePermissionGuard permission="staff.manage" allowedRoles={STAFF_ADMIN_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <StaffPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'teaching-load',
    handle: { crumb: 'Teaching Load' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={FACULTY_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TeachingLoadPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'leaves',
    handle: { crumb: 'Leave Requests' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={HR_READ_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <LeavesPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'payroll',
    handle: { crumb: 'Payroll & Payslips' },
    element: (
      <RoutePermissionGuard permission="hr.payroll.read" allowedRoles={PAYROLL_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <PayrollPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'marketplace',
    handle: { crumb: 'Tutor Marketplace' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={FACULTY_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TutorMarketplacePage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'reviews',
    handle: { crumb: 'Tutor Reviews & Ratings' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={FACULTY_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TutorReviewsPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'live-sessions',
    handle: { crumb: 'Live Tuition & OTPs' },
    element: (
      <RoutePermissionGuard permission="hr.read" allowedRoles={FACULTY_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <TeacherLiveSessionsPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
];

